'use client'

import {
  BeautyFrame,
  FullBleedImage,
  SafeText,
  beautyInk,
  beautyIvory,
  beautyMuted,
  beautySans,
  beautySerif,
  clampIndex,
  getBeautyCopy,
  getBeautyDesign,
  getBeautyVisual,
  type BeautyPackProps,
} from '../shared/BeautyPackCore'

const bg = [
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/story_01.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/story_02.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/story_03.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/story_04.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/story_05.jpg',
]

const total = 5

const fallbacks = [
  {
    eyebrow: 'Mi historia',
    headline: 'De no reconocer mi piel a sentirme segura conmigo.',
    subheadline:
      'Un camino real de transformación, constancia y cuidado propio.',
    cta: 'Ver historia',
  },
  {
    eyebrow: 'Antes',
    headline: 'Así se sentía mi piel antes.',
    subheadline:
      'No era solo textura. También era cómo me veía frente al espejo.',
    body:
      'Mi rutina se sentía confusa, pesada y poco constante.',
    bullets: [
      'Piel apagada e irregular',
      'Brotes y sensibilidad',
      'Demasiados productos sin claridad',
    ],
    beforeAfter: {
      before: 'Piel apagada, sensible y sin una rutina clara',
      after: 'Piel más calmada, luminosa y cuidada',
    },
  },
  {
    eyebrow: 'El cambio',
    headline: 'Dejé de perseguir soluciones rápidas.',
    subheadline:
      'El punto de giro fue simplificar mi rutina y volverla constante.',
    body:
      'Menos pasos, mejores decisiones y un ritual que sí podía sostener.',
    steps: [
      {
        label: '1',
        title: 'Simplifiqué mi rutina',
        text: 'Me quedé con lo esencial.',
      },
      {
        label: '2',
        title: 'Elegí mejor',
        text: 'Busqué lo que mi piel realmente necesitaba.',
      },
      {
        label: '3',
        title: 'Fui constante',
        text: 'Convertí el cuidado en un hábito diario.',
      },
    ],
    result: 'La constancia fue lo que cambió todo.',
  },
  {
    eyebrow: 'El progreso',
    headline: 'Pequeños pasos, progreso visible.',
    subheadline:
      'La transformación se notó poco a poco, no de golpe.',
    body:
      'Cada etapa confirmó que una rutina simple también puede ser poderosa.',
    timeline: [
      {
        label: 'Semana 1',
        title: 'Más calma',
        text: 'La piel empezó a sentirse más cómoda.',
      },
      {
        label: 'Semana 3',
        title: 'Más balance',
        text: 'Menos tirantez y mejor textura.',
      },
      {
        label: 'Semana 8',
        title: 'Más confianza',
        text: 'Volvió la seguridad frente al espejo.',
      },
    ],
    beforeAfter: {
      before: 'Antes: piel apagada y rutina inconsistente',
      after: 'Después: piel más luminosa y rutina sostenible',
    },
  },
  {
    eyebrow: 'El resultado',
    headline: 'Ahora mi piel cuenta otra historia.',
    subheadline:
      'No fue solo una transformación visible. También cambió cómo me sentía conmigo.',
    result:
      'Más que mejor piel: una relación más amable conmigo.',
    cta: 'Empieza tu ritual',
  },
]

function getRawSlide(draft: BeautyPackProps['draft'], index: number) {
  const slides = Array.isArray((draft as any)?.slides) ? (draft as any).slides : []
  return slides[index] || {}
}

function normalizeTextItem(item: any) {
  if (typeof item === 'string') return item.trim()

  if (item && typeof item === 'object') {
    return String(item.title || item.text || item.label || item.headline || '').trim()
  }

  return ''
}

function normalizeBullets(copy: any, fallback: any) {
  const source = Array.isArray(copy?.bullets) && copy.bullets.length
    ? copy.bullets
    : Array.isArray(fallback?.bullets)
      ? fallback.bullets
      : []

  return source.map(normalizeTextItem).filter(Boolean)
}

function normalizeSteps(copy: any, fallback: any) {
  const source = Array.isArray(copy?.steps) && copy.steps.length
    ? copy.steps
    : Array.isArray(copy?.bullets) && copy.bullets.length
      ? copy.bullets
      : Array.isArray(fallback?.steps) && fallback.steps.length
        ? fallback.steps
        : Array.isArray(fallback?.bullets)
          ? fallback.bullets
          : []

  return source
    .map((item: any, index: number) => {
      if (typeof item === 'string') {
        return {
          label: `0${index + 1}`,
          title: item.trim(),
          text: '',
        }
      }

      if (item && typeof item === 'object') {
        return {
          label: String(item.label || `0${index + 1}`).trim(),
          title: String(item.title || item.headline || item.text || '').trim(),
          text: String(item.text || item.body || item.description || '').trim(),
        }
      }

      return null
    })
    .filter((item: any) => item?.title || item?.text)
}

function normalizeTimeline(copy: any, fallback: any) {
  const source = Array.isArray(copy?.timeline) && copy.timeline.length
    ? copy.timeline
    : Array.isArray(copy?.steps) && copy.steps.length
      ? copy.steps
      : Array.isArray(fallback?.timeline) && fallback.timeline.length
        ? fallback.timeline
        : []

  return source
    .map((item: any, index: number) => {
      if (typeof item === 'string') {
        return {
          label: `Etapa ${index + 1}`,
          title: item.trim(),
          text: '',
        }
      }

      if (item && typeof item === 'object') {
        return {
          label: String(item.label || `Etapa ${index + 1}`).trim(),
          title: String(item.title || '').trim(),
          text: String(item.text || item.body || item.description || '').trim(),
        }
      }

      return null
    })
    .filter((item: any) => item?.label || item?.title || item?.text)
}

function StoryTopLabel({
  number,
  eyebrow,
  accent,
  light = false,
}: {
  number: string
  eyebrow: string
  accent: string
  light?: boolean
}) {
  return (
    <div className="absolute left-8 right-8 top-8 z-20 flex items-center gap-4">
      <SafeText
        className="text-[13px] font-bold uppercase tracking-[0.16em]"
        style={{
          color: light ? 'rgba(255,255,255,0.88)' : accent,
          fontFamily: beautySans,
        }}
      >
        {number}
      </SafeText>

      <div
        className="h-px flex-1"
        style={{
          background: light ? 'rgba(255,255,255,0.34)' : 'rgba(33,24,21,0.16)',
        }}
      />

      <SafeText
        className="max-w-[250px] text-right text-[11px] font-bold uppercase tracking-[0.20em]"
        style={{
          color: light ? 'rgba(255,255,255,0.88)' : accent,
          fontFamily: beautySans,
        }}
      >
        {eyebrow}
      </SafeText>
    </div>
  )
}

function StorySideImage({
  draft,
  index,
  fallbackImage,
  objectPosition = 'center',
}: {
  draft: BeautyPackProps['draft']
  index: number
  fallbackImage: string
  objectPosition?: string
}) {
  return (
    <div className="absolute right-0 top-0 h-full w-[44%] overflow-hidden">
      <FullBleedImage
        {...getBeautyVisual(draft, index, fallbackImage, {
          overlay: 'white',
          opacity: 0.06,
        })}
        objectPosition={objectPosition}
      />
    </div>
  )
}

function StoryBodySlide({
  draft,
  index,
  number,
  copy,
  accent,
  image,
  children,
}: {
  draft: BeautyPackProps['draft']
  index: number
  number: string
  copy: any
  accent: string
  image: string
  children: React.ReactNode
}) {
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: beautyIvory }}>
      <StorySideImage
        draft={draft}
        index={index}
        fallbackImage={image}
        objectPosition="72% center"
      />

      <div className="absolute inset-0 bg-gradient-to-r from-[#FFF9F0]/98 via-[#FFF9F0]/92 via-[58%] to-transparent" />

      <StoryTopLabel number={number} eyebrow={copy.eyebrow} accent={accent} />

      <div className="absolute left-8 top-[118px] z-20 w-[45%]" style={{ color: beautyInk }}>
        <SafeText
          className="text-[46px] font-normal leading-[0.92] tracking-[-0.052em]"
          style={{ fontFamily: beautySerif }}
        >
          {copy.headline}
        </SafeText>

        <SafeText
          className="mt-5 text-[17px] leading-[1.44]"
          style={{ color: 'rgba(33,24,21,0.76)', fontFamily: beautySans }}
        >
          {copy.subheadline || copy.body}
        </SafeText>

        <div
          className="mt-6 h-px w-[74px]"
          style={{ background: `${accent}88` }}
        />

        {children}
      </div>
    </div>
  )
}

function BulletList({
  items,
  accent,
}: {
  items: string[]
  accent: string
}) {
  return (
    <div className="mt-7 flex flex-col gap-4">
      {items.slice(0, 3).map((item, idx) => (
        <div key={`${idx}-${item}`} className="flex items-center gap-4">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-[13px] font-semibold"
            style={{
              borderColor: `${accent}66`,
              color: accent,
              background: 'rgba(255,255,255,0.62)',
              fontFamily: beautySans,
            }}
          >
            {idx + 1}
          </div>

          <SafeText
            className="flex-1 text-[15px] leading-[1.38]"
            style={{ color: beautyInk, fontFamily: beautySans }}
          >
            {item}
          </SafeText>
        </div>
      ))}
    </div>
  )
}

function StepList({
  items,
  accent,
}: {
  items: any[]
  accent: string
}) {
  return (
    <div className="mt-7 flex flex-col gap-4">
      {items.slice(0, 3).map((item, idx) => (
        <div key={`${idx}-${item.title || item.text}`} className="flex items-center gap-4">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-[12px] font-bold"
            style={{
              borderColor: `${accent}66`,
              color: accent,
              background: 'rgba(255,255,255,0.62)',
              fontFamily: beautySans,
            }}
          >
            {item.label || `0${idx + 1}`}
          </div>

          <div className="flex-1">
            <SafeText
              className="text-[15px] font-semibold leading-[1.22]"
              style={{ color: beautyInk, fontFamily: beautySans }}
            >
              {item.title || item.text}
            </SafeText>

            {item.text && item.text !== item.title ? (
              <SafeText
                className="mt-1 text-[13px] leading-[1.32]"
                style={{ color: beautyMuted, fontFamily: beautySans }}
              >
                {item.text}
              </SafeText>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  )
}

function TimelineList({
  items,
  accent,
}: {
  items: any[]
  accent: string
}) {
  return (
    <div className="mt-7 flex flex-col gap-4">
      {items.slice(0, 3).map((item, idx) => (
        <div key={`${idx}-${item.label}`} className="flex items-center gap-4">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-[12px] font-bold"
            style={{
              borderColor: `${accent}66`,
              color: idx === items.slice(0, 3).length - 1 ? '#fff' : accent,
              background:
                idx === items.slice(0, 3).length - 1
                  ? accent
                  : 'rgba(255,255,255,0.62)',
              fontFamily: beautySans,
            }}
          >
            {idx + 1}
          </div>

          <div className="flex-1">
            <SafeText
              className="text-[12px] font-bold uppercase tracking-[0.12em]"
              style={{ color: accent, fontFamily: beautySans }}
            >
              {item.label}
            </SafeText>

            <SafeText
              className="mt-1 text-[14px] leading-[1.32]"
              style={{ color: beautyInk, fontFamily: beautySans }}
            >
              {item.text || item.title}
            </SafeText>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function BeautyStoryPack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
  showControls = true,
  exportMode = false,
}: BeautyPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getBeautyDesign(draft, '#B18A4A')
  const accent = design.accent || '#B18A4A'

  const rawSlide = getRawSlide(draft, index)
  const copy = getBeautyCopy(draft, index, fallbacks[index])
  const bullets = normalizeBullets(copy, fallbacks[index])
  const steps = normalizeSteps(copy, fallbacks[index])
  const timeline = normalizeTimeline(copy, fallbacks[index])
  const beforeAfter = (rawSlide as any)?.beforeAfter || (copy as any)?.beforeAfter || (fallbacks[index] as any)?.beforeAfter || null

  return (
    <BeautyFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      showControls={showControls}
      nav={index === 0 || index === 4 ? 'light' : 'dark'}
      className="bg-[#F6EEE3]"
      borderRadius={exportMode ? 0 : 34}
    >
      {index === 0 ? (
        <>
          <FullBleedImage
            {...getBeautyVisual(draft, index, bg[0], {
              overlay: 'black',
              opacity: 0.12,
            })}
            objectPosition="68% center"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-black/68 via-black/34 to-transparent" />

          <StoryTopLabel
            number="01"
            eyebrow={copy.eyebrow || 'Mi historia'}
            accent={accent}
            light
          />

          <div className="absolute bottom-[112px] left-8 z-20 max-w-[74%] text-white">
            <SafeText
              className="text-[60px] font-normal leading-[0.90] tracking-[-0.058em]"
              style={{ fontFamily: beautySerif }}
            >
              {copy.headline}
            </SafeText>

            <SafeText
              className="mt-6 max-w-[78%] text-[18px] leading-[1.42] text-white/86"
              style={{ fontFamily: beautySans }}
            >
              {copy.subheadline || copy.body}
            </SafeText>
          </div>

          <div
            className="absolute bottom-11 left-8 z-20 text-[12px] font-bold uppercase tracking-[0.24em] text-white/78"
            style={{ fontFamily: beautySans }}
          >
            {copy.cta || 'Ver historia'}
          </div>
        </>
      ) : null}

      {index === 1 ? (
        <StoryBodySlide
          draft={draft}
          index={index}
          number="02"
          copy={copy}
          accent={accent}
          image={bg[1]}
        >
          <BulletList items={bullets} accent={accent} />

          {beforeAfter ? (
            <div
              className="mt-7 rounded-[18px] border px-4 py-4"
              style={{
                borderColor: `${accent}44`,
                background: 'rgba(255,255,255,0.50)',
              }}
            >
              <SafeText
                className="text-[11px] font-bold uppercase tracking-[0.18em]"
                style={{ color: accent, fontFamily: beautySans }}
              >
                Antes
              </SafeText>

              <SafeText
                className="mt-2 text-[13px] leading-[1.34]"
                style={{ color: beautyMuted, fontFamily: beautySans }}
              >
                {beforeAfter.before}
              </SafeText>
            </div>
          ) : null}
        </StoryBodySlide>
      ) : null}

      {index === 2 ? (
        <StoryBodySlide
          draft={draft}
          index={index}
          number="03"
          copy={copy}
          accent={accent}
          image={bg[2]}
        >
          <StepList items={steps} accent={accent} />

          {(copy.result || copy.proof) ? (
            <SafeText
              className="mt-7 text-[12px] font-bold uppercase leading-[1.42] tracking-[0.16em]"
              style={{ color: accent, fontFamily: beautySans }}
            >
              {copy.result || copy.proof}
            </SafeText>
          ) : null}
        </StoryBodySlide>
      ) : null}

      {index === 3 ? (
        <StoryBodySlide
          draft={draft}
          index={index}
          number="04"
          copy={copy}
          accent={accent}
          image={bg[3]}
        >
          <TimelineList items={timeline} accent={accent} />

          {beforeAfter?.after ? (
            <SafeText
              className="mt-7 text-[12px] font-bold uppercase leading-[1.42] tracking-[0.16em]"
              style={{ color: accent, fontFamily: beautySans }}
            >
              {beforeAfter.after}
            </SafeText>
          ) : null}
        </StoryBodySlide>
      ) : null}

      {index === 4 ? (
        <>
          <FullBleedImage
            {...getBeautyVisual(draft, index, bg[4], {
              overlay: 'black',
              opacity: 0.12,
            })}
            objectPosition="70% center"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-[#4A3324]/86 via-[#4A3324]/54 to-transparent" />

          <StoryTopLabel
            number="05"
            eyebrow={copy.eyebrow || 'El resultado'}
            accent={accent}
            light
          />

          <div className="absolute bottom-[132px] left-8 z-20 max-w-[72%] text-white">
            <SafeText
              className="text-[56px] font-normal leading-[0.90] tracking-[-0.058em]"
              style={{ fontFamily: beautySerif }}
            >
              {copy.result || copy.headline}
            </SafeText>

            <SafeText
              className="mt-7 max-w-[82%] text-[18px] leading-[1.45] text-white/86"
              style={{ fontFamily: beautySans }}
            >
              {copy.subheadline || copy.body}
            </SafeText>
          </div>

          <div className="absolute bottom-12 left-8 z-20">
            <div
              className="border px-6 py-4 text-[12px] font-bold uppercase tracking-[0.18em] text-white"
              style={{
                borderColor: 'rgba(255,255,255,0.44)',
                fontFamily: beautySans,
              }}
            >
              {copy.cta || 'Empieza tu ritual'}
            </div>
          </div>
        </>
      ) : null}
    </BeautyFrame>
  )
}